import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { FaShoppingCart } from "react-icons/fa";
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from "react-i18next";
import SearchOverlay from "./SearchOverlay";

/* --- Styled Components --- */
const Header = tw.header`flex justify-between items-center max-w-screen-xl mx-auto mt-[10px]`;

const NavLinks = tw.div`flex items-center gap-2`;
const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#4e7662] hocus:text-[#000000]
`;
const PrimaryLink = tw(
  NavLink
)`lg:mx-0 px-8 py-3 rounded bg-[#4e7662] text-gray-100 hocus:bg-[#4e7662] hocus:text-gray-200 focus:shadow-outline border-b-0`;

const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};
  img {
    width: 130px;
    height: auto;
    ${tw`mr-3`}
  }
`;

const CartIcon = styled(FaShoppingCart)`
  ${tw`text-gray-800 hover:text-[#4e7662] transition duration-300`};
  font-size: 18px;
  margin-left: 8px;
`;

const CollectionDropdown = styled.div`${tw`relative`};`;
const CollectionMenu = styled.div`
  ${tw`absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20`};
  display: ${({ open }) => (open ? "block" : "none")};

  @media (max-width: 1024px) {
    position: relative;
    box-shadow: none;
    background: transparent;
    margin-top: 0.5rem;
  }
`;

const NavLinkStyled = styled(Link)`
  ${tw`block px-6 py-4 text-gray-800 font-semibold text-base transition-colors duration-200`};
  text-align: left;
  border-radius: 8px;
  margin: 2px 12px;
  &:hover {
    background-color: #4e7662;
    color: #fff;
  }
`;

const DropdownMenu = styled.div`
  ${tw`absolute mt-2 rounded-xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5`};
  z-index: 100;
  right: 0;
  min-width: 240px;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
`;

const LogoutButton = styled.button`
  ${tw`px-6 py-4 text-gray-800 font-semibold text-base text-left w-full transition-colors duration-200`};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  margin: 2px 12px;
  &:hover {
    background-color: #4e7662;
    color: #fff;
  }
`;

const UserButton = styled.button`
  ${tw`relative px-4 py-2 rounded-full bg-[#4e7662] text-white font-semibold mr-4 transition duration-300`};
  &:hover {
    background-color: #4e7662;
    color: #ffffff;
  }
  &:focus {
    ${tw`outline-none`}
  }
`;

/* --- Mobile Styles --- */
const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between lg:hidden`;
const NavToggle = tw.button`lg:hidden z-20 focus:outline-none transition duration-300`;
const MobileNavLinks = motion(styled.div`
  position: absolute;
  top: 170px;
  right: 0;
  width: 16rem;
  padding: 1.5rem;
  background-color: white;
  color: #1f2937;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
`);
const MobileNavLinksList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;
const DesktopNavLinks = tw.nav`hidden lg:flex flex-1 justify-between items-center`;

/* --- Search Icon Button --- */
const SearchButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #1f2937;
  font-size: 18px;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin-left: 4px;
  transition: color 0.3s;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:hover { color: #4e7662; background: none; }
  &:focus { outline: none; box-shadow: none; background: none; }
  &:active { outline: none; background: none; box-shadow: none; }
  &:focus-visible { outline: none; box-shadow: none; background: none; }
`;
export default ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false); // ✅ Search state
  const { showNavLinks, toggleNavbar } = useAnimatedNavToggler();
  const navigate = useNavigate();
  const dropdownRef = useRef();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".collection-dropdown")) setCollectionOpen(false);
      if (!e.target.closest(".user-dropdown")) setDropdownOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate("/");
  };

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src={logo} alt="logo" />
    </LogoLink>
  );
  logoLink = logoLink || defaultLogoLink;

  links = [
    <NavLinks key="left" style={{ flex: 1 }}>
      <NavLink href="/">{t("Accueil")}</NavLink>

      <CollectionDropdown className="collection-dropdown">
        <NavLink
          as="div"
          style={{ cursor: "pointer" }}
          onClick={() => setCollectionOpen((prev) => !prev)}
        >
          {t("Notre_Collection")}
        </NavLink>

        {collectionOpen && (
          <CollectionMenu open={collectionOpen}>
            <NavLinkStyled to="/Cosmetique">{t("Cosmetique")}</NavLinkStyled>
            <NavLinkStyled to="/Packs">{t("Packs")}</NavLinkStyled>
            <NavLinkStyled to="/Complements">{t("Complements")}</NavLinkStyled>
            <NavLinkStyled to="/Shampoing-1l">{t("Shampoing_1L")}</NavLinkStyled>
            <NavLinkStyled to="/Garence">{t("Garence")}</NavLinkStyled>
          </CollectionMenu>
        )}
      </CollectionDropdown>
    </NavLinks>,

    logoLink,

    <NavLinks key="right" style={{ flex: 1, justifyContent: "flex-end" }}>
      <NavLink href="/à-props">{t("à_propos")}</NavLink>
      <NavLink href="/contact">{t("Contact")}</NavLink>

      {/* ✅ Search icon - desktop (next to cart) */}
      <SearchButton onClick={() => setSearchOpen(true)}>
        <i className="bi bi-search" />
      </SearchButton>

      <NavLink href="/checkout" style={{ display: "flex", alignItems: "center" }}>
        <CartIcon />
      </NavLink>

      {!user ? (
        <>
          <NavLink href="/login">{t("Connexion")}</NavLink>
          <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href="/signup">
            {t("Inscription")}
          </PrimaryLink>
        </>
      ) : (
        <div tw="relative inline-block" className="user-dropdown">
          <UserButton onClick={() => setDropdownOpen(!dropdownOpen)}>
            {user.name}
          </UserButton>
          {dropdownOpen && (
            <DropdownMenu>
              <NavLinkStyled to="/checkout">{t("Paiement")}</NavLinkStyled>
              <NavLinkStyled to="/dashboard">{t("Tableau_de_bord")}</NavLinkStyled>
              <NavLinkStyled to="/profile">{t("Profile")}</NavLinkStyled>
              <NavLinkStyled to="/point">{t("point")}</NavLinkStyled>


              <LogoutButton onClick={handleLogout}>{t("Déconnexion")}</LogoutButton>
            </DropdownMenu>
          )}
        </div>
      )}
    </NavLinks>,
  ];

  return (
    <>
      {/* ✅ SearchOverlay inside the return, wrapped in fragment */}
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      <Header className={className || "header-light"}>
        {/* --- Desktop Navigation --- */}
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {links}
        </DesktopNavLinks>

        {/* --- Mobile Navigation --- */}
        <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
          {logoLink}
          <div className="relative">
            <NavToggle onClick={toggleNavbar}>
              {showNavLinks ? (
                <CloseIcon tw="w-6 h-6" />
              ) : (
                <MenuIcon tw="w-6 h-6" />
              )}
            </NavToggle>

            <AnimatePresence>
              {showNavLinks && (
                <MobileNavLinks
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <MobileNavLinksList>
                    <NavLink href="/">{t("Accueil")}</NavLink>

                    <div className="collection-dropdown">
                      <NavLink
                        as="div"
                        style={{ cursor: "pointer" }}
                        onClick={() => setCollectionOpen((prev) => !prev)}
                      >
                        {t("Notre_Collection")}
                      </NavLink>

                      {collectionOpen && (
                        <CollectionMenu open={collectionOpen}>
                          <NavLinkStyled to="/Cosmetique">{t("Cosmetique")}</NavLinkStyled>
                          <NavLinkStyled to="/Packs">{t("Packs")}</NavLinkStyled>
                          <NavLinkStyled to="/Complements">{t("Complements")}</NavLinkStyled>
                          <NavLinkStyled to="/Shampoing-1l">{t("Shampoing_1L")}</NavLinkStyled>
                          <NavLinkStyled to="/Garence">{t("Garence")}</NavLinkStyled>
                        </CollectionMenu>
                      )}
                    </div>

                    <NavLink href="/à-props">{t("à_propos")}</NavLink>
                    <NavLink href="/contact">{t("Contact")}</NavLink>

                    {/* ✅ Search icon - mobile */}
                    <SearchButton onClick={() => setSearchOpen(true)}>
                      <i className="bi bi-search" />
                      <span style={{ marginLeft: "6px", fontSize: "14px" }}>
                        {t("Rechercher") || "Rechercher"}
                      </span>
                    </SearchButton>

                    <NavLink href="/checkout" style={{ display: "flex", alignItems: "center" }}>
                      <CartIcon />
                    </NavLink>

                    {!user ? (
                      <>
                        <NavLink href="/login">{t("Connexion")}</NavLink>
                        <PrimaryLink href="/signup">{t("Inscription")}</PrimaryLink>
                      </>
                    ) : (
                      <>
                        <NavLink href="/checkout">{t("Paiement")}</NavLink>
                        <NavLink href="/dashboard">{t("Tableau_de_bord")}</NavLink>
                        <NavLink href="/profile">{t("Profile")}</NavLink>
                        <NavLink href="/" onClick={handleLogout}>
                          {t("Déconnexion")}
                        </NavLink>
                      </>
                    )}
                  </MobileNavLinksList>
                </MobileNavLinks>
              )}
            </AnimatePresence>
          </div>
        </MobileNavLinksContainer>
      </Header>
    </>
  );
};

/* --- Breakpoint Config --- */
const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};